import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CustomLoader from "../static/CustomLoader";
import SucessModal from "../successModal/SuccessModal";

const NAVY = "#123B5D";
const BORDER = "#DCE4EC";
const MUTED = "#5B6B7A";

const Dropdown = ({ selectedId, mode, onHide, setRefresh }) => {
  const apiUrl = localStorage.getItem("apiUrl");
  const dbTokenData = localStorage.getItem("dbTokenData");
  const headers = {
    Authorization: `Bearer ${dbTokenData}`,
    "Content-Type": "application/json",
    dbMaster: "",
  };
  const [dropdownList, setDropdownList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [title, setTitle] = useState("");

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
    setTitle("");
    onHide();
    setRefresh(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiUrl}dropdown/fxid/${selectedId}`, {
          headers,
        });
        if (Array.isArray(res.data)) {
          setDropdownList(res.data);
        } else {
          toast.error("Invalid response from server.");
        }
      } catch (err) {
        toast.error("Error fetching dropdown data.");
      } finally {
        setLoading(false);
      }
    };
    if (selectedId) fetchData();
  }, [selectedId]);

  const addNewRow = () => {
    const maxCode = dropdownList.reduce(
      (max, row) => Math.max(max, row.code || 0),
      0,
    );
    const newRow = {
      id: 0,
      fxid: selectedId,
      code: maxCode + 1,
      value: "",
      comment: "",
    };
    setDropdownList([...dropdownList, newRow]);
  };

  const deleteRow = (index) => {
    const updatedList = [...dropdownList];
    updatedList.splice(index, 1);
    setDropdownList(updatedList);
  };

  const handleChange = (index, field, value) => {
    const updated = [...dropdownList];
    updated[index][field] = value;
    setDropdownList(updated);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${apiUrl}dropdown/saveorupdate`,
        dropdownList,
        { headers },
      );
      if (res.data.cd === 1) {
        setSuccessMessage(res.data.description);
        setTitle("⚠️ Failed");
      } else {
        setSuccessMessage(
          mode === "A"
            ? "Dropdown values added successfully."
            : "Dropdown values updated.",
        );
        setTitle("✔️ Success");
      }
    } catch (err) {
      setSuccessMessage("Server error during save.");
      setTitle("❌ Error");
    } finally {
      setLoading(false);
      setShowSuccessModal(true);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 38,
      borderRadius: "7px",
      fontSize: 13,
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#CBD5DF",
      },
      "&:hover fieldset": {
        borderColor: "#AEBBC8",
      },
      "&.Mui-focused fieldset": {
        borderColor: NAVY,
        boxShadow: "0 0 0 3px rgba(18,59,93,0.08)",
      },
    },
    "& .MuiInputBase-input": {
      px: 1.25,
      py: 0,
      fontSize: 13,
    },
  };

  return (
    <>
      <SucessModal
        show={showSuccessModal}
        onClose={handleCloseSuccessModal}
        headerText={title}
        message={successMessage}
      />

      {loading ? (
        <CustomLoader />
      ) : (
        <Box
          sx={{
            width: "100%",
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 2 },
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: NAVY,
                  lineHeight: 1.3,
                }}
              >
                Dropdown Values
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 12,
                  color: MUTED,
                }}
              >
                Manage values for: <strong>{selectedId}</strong>
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddRoundedIcon />}
              onClick={addNewRow}
              sx={{
                height: 36,
                px: 1.5,
                borderRadius: "8px",
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: "#2E9E6B",
                "&:hover": {
                  backgroundColor: "#278A5C",
                },
              }}
            >
              Add Row
            </Button>
          </Box>

          <TableContainer
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              border: `1px solid ${BORDER}`,
              borderRadius: "8px",
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F8FAFC",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#CBD5E1",
                borderRadius: 10,
              },
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 650,
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: 80,
                      py: 1,
                      px: 1,
                      backgroundColor: NAVY,
                      color: "#FFFFFF",
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    Code
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "35%",
                      py: 1,
                      px: 1,
                      backgroundColor: NAVY,
                      color: "#FFFFFF",
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    Value
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "55%",
                      py: 1,
                      px: 1,
                      backgroundColor: NAVY,
                      color: "#FFFFFF",
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    Comment
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 64,
                      py: 1,
                      px: 1,
                      backgroundColor: NAVY,
                      color: "#FFFFFF",
                      fontSize: 12.5,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Delete
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dropdownList.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      "& td": {
                        borderBottom: "1px solid #EEF2F6",
                      },
                      "&:hover": {
                        backgroundColor: "#F7FAFC",
                      },
                    }}
                  >
                    <TableCell sx={{ p: 0.75 }}>
                      <TextField
                        type="number"
                        value={row.code}
                        fullWidth
                        size="small"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.75 }}>
                      <TextField
                        value={row.value}
                        onChange={(e) =>
                          handleChange(index, "value", e.target.value)
                        }
                        fullWidth
                        size="small"
                        placeholder="Enter value"
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.75 }}>
                      <TextField
                        value={row.comment}
                        onChange={(e) =>
                          handleChange(index, "comment", e.target.value)
                        }
                        fullWidth
                        size="small"
                        placeholder="Enter comment"
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0.75,
                        textAlign: "center",
                      }}
                    >
                      <IconButton
                        onClick={() => deleteRow(index)}
                        aria-label="Delete row"
                        title="Delete row"
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "7px",
                          color: "#D64545",
                          backgroundColor: "#FFF3F3",
                          border: "1px solid #F0C7C7",
                          "&:hover": {
                            backgroundColor: "#FDE6E6",
                            color: "#B93838",
                          },
                        }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="bottombtn">
            <button
              className="btn btn-success"
              disabled={loading}
              onClick={handleSubmit}
            >
              Save
            </button>
            {loading && <CustomLoader />}
            <button className="btn btn-danger" onClick={onHide}>
              Cancel
            </button>
          </div>
        </Box>
      )}
    </>
  );
};

export default Dropdown;
